import { Component, Input, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { IceDatabaseService } from 'src/app/services/ice-database.service';
import { ChildColumnReference, Fields, FilterData, ParentColumnReference, UpdateTableReq } from '../../models';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { IceapiService } from 'src/app/services';
import { CustomToastService } from 'src/app/services/custom-toast.service';
import { v4 as uuidv4 } from 'uuid';

@Component({
    selector: 'app-ice-details',
    templateUrl: './ice-details.component.html',
    styleUrls: ['./ice-details.component.scss'],
})
export class IceDetailsComponent implements OnInit {
    @Input() type: string;
    @Input() tableName: string;
    @Input() rowData: any;
    @Input() column: Fields[];
    @Input() childColumnReferences: ChildColumnReference[];
    @Input() parentColumnReferences: ParentColumnReference[];
    @Input() tableFilterList: FilterData[];

    childColumns: string[];
    parentColumns: string[];
    tableFormGroup: FormGroup;
    data: any[] = [];
    isSubmit: boolean = false;

    constructor(
        public ngbActiveModal: NgbActiveModal,
        private iceDatabaseService: IceDatabaseService,
        private formBuilder: FormBuilder,
        private iceapiService: IceapiService,
        private customToastService: CustomToastService
    ) {}

    ngOnInit(): void {
        this.initialization();
    }

    initialization(): void {
        this.parentColumns = this.parentColumnReferences.map((x) => x.fieldName);
        this.childColumns = this.childColumnReferences.map((x) => x.fieldName);
        this.tableFormGroup = this.formBuilder.group({});

        if (this.type === 'Add') {
            this.column = this.column.filter((item) => !this.childColumns.includes(item.field));
        }

        this.column.forEach((fieldObj, index) => {
            const isParentColumn = this.parentColumns.includes(fieldObj.field);
            let value = this.rowData ? (isParentColumn ? this.getParentColumnValue(fieldObj.field) : this.rowData[fieldObj.field]) : '';
            if (index == 0 && (value == '' || !value)) {
                value = uuidv4();
            }
            const dataItem: any = { key: fieldObj.field, value, isRequire: fieldObj.isRequire };

            if (isParentColumn) {
                const parent = this.parentColumnReferences.find((x) => x.fieldName === fieldObj.field);
                dataItem.data = this.tableFilterList.find((x) => x.title === parent.parentTableName)?.data;
                dataItem.filterList = [...dataItem.data];
                dataItem.searchKey = null;
            }

            this.data.push(dataItem);

            this.tableFormGroup.addControl(
                fieldObj.field,
                this.formBuilder.control(
                    { value: value, disabled: this.type === 'Details' || (this.type !== 'Details' && index == 0) ? true : false },
                    fieldObj.isRequire ? [Validators.required] : []
                )
            );
        });
    }

    getParentColumnValue(key): string {
        const parent = this.parentColumnReferences.find((x) => x.fieldName === key);
        return this.rowData[parent.childColumnName.charAt(0).toLowerCase() + parent.childColumnName.slice(1)];
    }

    openParentDetails(columnName: string): void {
        this.iceDatabaseService.openParentDetails(columnName, this.rowData, this.parentColumnReferences);
    }

    openEdit(): void {
        this.type = 'Edit';
        this.data.forEach((obj, index) => {
            if (index != 0) {
                this.tableFormGroup.get(obj.key).enable();
            }
        });
    }

    deleteDetails(): void {
        this.iceDatabaseService.deleteData(
            this.tableName,
            this.rowData,
            this.column.map((x) => x.field)
        );
    }

    openChildDetails(columnName: string): void {
        if (this.type != 'Edit') {
            const child = this.childColumnReferences.find((x) => x.fieldName == columnName);
            const data = {
                tableName: child.originalFieldName,
                fieldName: child.parentFieldName,
                id: this.rowData[this.column[0].field],
            };
            this.ngbActiveModal.close({ type: this.type, data: data });
        } else {
            this.customToastService.errorToast("Can't navigate away while editing.", 'Error');
        }
    }

    updateOrSaveTableDetails(isUpdate: boolean): void {
        this.isSubmit = true;
        if (this.tableFormGroup.valid) {
            const payload = {};
            for (const key in this.tableFormGroup.controls) {
                if (this.tableFormGroup.controls.hasOwnProperty(key) && (this.tableFormGroup.controls[key].dirty || this.data[0].key === key)) {
                    if (!this.parentColumns.includes(key)) {
                        payload[key] = this.tableFormGroup.controls[key].value;
                    } else {
                        const parent = this.parentColumnReferences.find((x) => x.fieldName === key);
                        payload[parent.childColumnName] = this.tableFormGroup.controls[key].value;
                    }
                }
            }
            const updatePayload: UpdateTableReq = {
                tableName: this.tableName,
                updatedTableData: JSON.stringify(payload),
                isDelete: false,
            };

            const apiServiceMethod = isUpdate ? 'UpdateIceDatabaseTable' : 'InsertIceDatabaseTable';
            const successMessage = isUpdate ? 'Data updated successfully!' : 'Successfully added new entry into the table!';
            const errorMessage = isUpdate
                ? 'Unable to update data. Please try again later or contact administrator.'
                : 'Unable to add new item. Please try again later or contact administrator.';

            this.iceapiService[apiServiceMethod](updatePayload).subscribe((res) => {
                if (res.success) {
                    this.ngbActiveModal.close({ type: this.type, data: { tableName: this.tableName } });
                    this.customToastService.successToast(successMessage, 'Success');
                } else {
                    this.customToastService.errorToast(errorMessage, 'Error');
                }
            });
        } else {
            return;
        }
    }

    allItems(): void {
        this.ngbActiveModal.close({ type: '', data: { tableName: this.tableName } });
    }

    searchResult(event, item) {
        this.searchItem(item);

        if (event.key === 'Backspace') {
            item.filterList = item.data;
            this.searchItem(item);
        }
    }

    searchItem(item) {
        if (item.searchKey) {
            item.filterList = item.data.filter((x) => {
                return (
                    x.value?.trim().toLowerCase().replace(/\s+/g, '').includes(item.searchKey?.trim().toLowerCase().replace(/\s+/g, '')) ||
                    x.id?.trim().toLowerCase().replace(/\s+/g, '').includes(item.searchKey?.trim().toLowerCase().replace(/\s+/g, ''))
                );
            });
        }
    }

    optionCloased(item) {
        item.searchKey = null;
        item.filterList = item.data;
    }
}
